import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import type { Invoice, Payload } from '../../../components/ModalInvoicePenaltyFee'
import { ModalInvoicePenaltyFee } from '../../../components/Modals'
import { CardCreditInterestFee, type PayloadValues } from '../Modals/CardCreditInterestFee'
import { type Filters, RollingCreditFilters, type RollingCreditFiltersProps } from '../RollingCreditFilters'
import { RollingCreditTable, type RollingCreditType } from '../RollingCreditTable'

type RollingCreditProps = {
  credits: RollingCreditType[]
  onApiFilterChange?: (filters: Partial<Filters>) => void
  onMarkAsClosed?: (id: string) => Promise<unknown>
  onMarkAsPaidEnough?: (row: RollingCreditType) => Promise<unknown>
  options?: RollingCreditFiltersProps['options']
  onNotification?: (message: string) => void
  onCreateLatePaymentInvoice?: (payload: Payload) => unknown | Promise<unknown>
  isMarkAsClosedDisabled?: (row: RollingCreditType) => boolean
  isMarkAsPaidEnoughDisabled?: (row: RollingCreditType) => boolean
  isLoading?: boolean
  onRecalculateTransactions?: (creditId: string) => unknown | Promise<unknown>
  onCreateCardCreditIntrestFee?: (payload: PayloadValues) => unknown | Promise<unknown>
  onModalOpen?: (creditId: string) => void
  onModalClose?: () => void
}

export const unique = <T,>(data: T[]) => Array.from(new Set(data))

export const extractOptions = (credits: RollingCreditType[]) => {
  const firstPass = credits.reduce<{ currency: string[]; status: string[]; receiver: string[]; type: string[] }>(
    (acc, credit) => ({
      currency: [...acc.currency, credit.currency],
      status: [...acc.status, credit.status],
      receiver: [...acc.receiver, credit.receiver],
      type: [...acc.type, credit.type],
    }),
    { currency: [], status: [], receiver: [], type: [] },
  )

  const currencies = unique(firstPass.currency)
  const statuses = unique(firstPass.status)
  const receivers = unique(firstPass.receiver).filter(Boolean)
  const types = unique(firstPass.type).filter(Boolean)

  return {
    currency: currencies.map((currency) => ({ label: currency, value: currency })),
    status: statuses.map((states) => ({ label: states, value: states })),
    receiver: receivers.map((receiver) => ({ label: receiver, value: receiver })),
    type: types.map((type) => ({ label: type, value: type })),
  }
}

const mapRollingCreditToInvoice = (credit: RollingCreditType): Invoice => ({
  dueDate: credit.dueDate,
  amount: {
    currency: credit.currency,
    value: 0,
  },
  id: credit.creditId.toString(),
  latePaymentInterestRate: {
    currency: credit.currency,
    value: 0,
  },
  no: Number(credit.creditNo),
})

const formatDates = (field: string, date: string) => {
  if (field === 'period') return dayjs(date).format('MM-DD')

  return dayjs(date).format('YYYY-MM-DD')
}

type SelectedInvoice = {
  rollingCredit: RollingCreditType
  invoice: Invoice
}

type ModalNames = 'invoicePenaltyFee' | 'cardCreditInterestFee'

export const RollingCredit = ({
  credits,
  onApiFilterChange,
  onMarkAsClosed,
  onMarkAsPaidEnough,
  options,
  onNotification,
  onCreateLatePaymentInvoice,
  onCreateCardCreditIntrestFee,
  onModalOpen,
  onModalClose,
  ...props
}: RollingCreditProps) => {
  // Receiver and account type filters are applied client-side, so their options are always derived from the loaded rows.
  const _options: RollingCreditFiltersProps['options'] = useMemo(() => {
    const derived = extractOptions(credits)

    return {
      currency: options?.currency ?? derived.currency,
      status: options?.status ?? derived.status,
      receiver: derived.receiver,
      type: derived.type,
    }
  }, [credits, options])

  const [filters, setFilters] = useState<Partial<Filters>>({
    hasPaid: false,
    state: 'OPEN',
    invoiceDate: {
      from: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
    },
  })
  const [selectedInvoice, setSelectedInvoice] = useState<Partial<Record<ModalNames, SelectedInvoice>>>()

  const handleFilterChange = (filters: Partial<Filters>) => {
    setFilters(filters)
    onApiFilterChange?.(filters)
  }

  const handleOpenInvoice = (modal: ModalNames, rollingCredit: RollingCreditType) => {
    onModalOpen?.(rollingCredit.creditId)

    setSelectedInvoice({
      [modal]: {
        invoice: mapRollingCreditToInvoice(rollingCredit),
        rollingCredit,
      },
    })
  }

  const handleCreateLatePaymentInvoice = async (payload: Payload) => {
    const promiseOrVoid = onCreateLatePaymentInvoice?.(payload)

    if (promiseOrVoid instanceof Promise) {
      promiseOrVoid.then(() => {
        setSelectedInvoice(undefined)
        onModalClose?.()
      })
    }

    return promiseOrVoid
  }

  const handleCreateCardCreditIntrestFee = async (payload: PayloadValues) => {
    const promiseOrVoid = onCreateCardCreditIntrestFee?.(payload)

    if (promiseOrVoid instanceof Promise) {
      promiseOrVoid.then(() => {
        setSelectedInvoice(undefined)
        onModalClose?.()
      })
    }

    return promiseOrVoid
  }

  // Receiver and account type are filtered on the client since the API has no params for them.
  const filteredCredits = useMemo(
    () =>
      credits.filter((credit) => {
        if (filters.receiver && credit.receiver !== filters.receiver) return false
        if (filters.type && credit.type !== filters.type) return false

        return true
      }),
    [credits, filters.receiver, filters.type],
  )

  const handleMarkAsClosed = async (id: string) => onMarkAsClosed?.(id)

  const handleMarkAsPaidEnough = async (row: RollingCreditType) => onMarkAsPaidEnough?.(row)

  const _format = (value: number, currency: string) =>
    Intl.NumberFormat('sv', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
    }).format(value)

  return (
    <Container>
      {selectedInvoice?.cardCreditInterestFee && (
        <CardCreditInterestFee
          isOpen
          onModalCancel={() => {
            setSelectedInvoice(undefined)
            onModalClose?.()
          }}
          onSubmit={handleCreateCardCreditIntrestFee}
          companyName={selectedInvoice.cardCreditInterestFee.rollingCredit.customer}
          defaultValues={{
            interest: {
              currency: selectedInvoice.cardCreditInterestFee.invoice.amount.currency,
              value: 0,
            },
            vat: {
              currency: selectedInvoice.cardCreditInterestFee.invoice.amount.currency,
              value: 0,
            },
          }}
        />
      )}
      {selectedInvoice?.invoicePenaltyFee && (
        <ModalInvoicePenaltyFee
          isOpen
          onModalCancel={() => {
            setSelectedInvoice(undefined)
            onModalClose?.()
          }}
          onSubmit={handleCreateLatePaymentInvoice}
          companyName={selectedInvoice?.invoicePenaltyFee.rollingCredit.customer}
          formatAmount={_format}
          defaultPenaltyFee={{
            currency: selectedInvoice?.invoicePenaltyFee?.invoice.amount.currency,
            latePaymentFee: {
              currency: selectedInvoice?.invoicePenaltyFee?.invoice.amount.currency,
              value: 0,
            },
            vat: {
              currency: selectedInvoice?.invoicePenaltyFee?.invoice.amount.currency,
              value: 0,
            },
          }}
          invoice={selectedInvoice?.invoicePenaltyFee?.invoice}
        />
      )}
      <RollingCreditFilters dateFormat="YYYY-MM-DD" onFilterChange={handleFilterChange} options={_options} filterValues={filters} />
      <RollingCreditTable
        {...props}
        formatAmount={_format}
        formatDate={formatDates}
        textSearch={filters.text}
        rollingCredits={filteredCredits}
        onCreateLatePaymentFee={(row) => handleOpenInvoice('invoicePenaltyFee', row)}
        onCreateCreditInterestFee={(row) => handleOpenInvoice('cardCreditInterestFee', row)}
        onMarkAsClosed={handleMarkAsClosed}
        onMarkAsPaidEnough={handleMarkAsPaidEnough}
      />
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
